// src/pages/CourseTime/hooks/useSessionData.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchProgramLayoutMock } from '../../../services/_mock_api/fetch_program_layout.mock';
import { fetchSessionContentMock } from '../../../services/_mock_api/fetch_session_content.mock';
import {
    fetchLearningProgramContent,
    fetchLearningProgramOutline,
    updateContentHistory,
    finishSessionAssessmentsService
} from '../../../services/learning_program.service';
import { LOCAL_STORAGE_KEYS } from '../../../utils/constants';
import { sortSessionContent } from '../utils/sortSegments';
import { deriveSlideLayouts } from '../utils/deriveSlideLayout';

export const useSessionData = (identifier, profile) => {
  const [loading, setLoading] = useState(true);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [programLayout, setProgramLayout] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [sessionContent, setSessionContent] = useState(null);
  const [sloTitles, setSloTitles] = useState(null);
  const [courseTitle, setCourseTitle] = useState('');
  const [activeSessionNo, setActiveSessionNo] = useState(null);
  const [activeSegment, setActiveSegment] = useState(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [positionUpdateError, setPositionUpdateError] = useState(null);
  const [lastMessageReadTimestamp, setLastMessageReadTimestamp] = useState(null);
  const [discussionSessions, setDiscussionSessions] = useState([]);
  const [sessionAssessments, setSessionAssessments] = useState({});

  // const [introVideo, setIntroVideo] = useState(null);

  const sessionJustLoadedRef = useRef(false);
  // Tracks which session the currently-loaded sessionContent belongs to. Used to
  // gate session auto-completion: while switching sessions, activeSessionNo updates
  // immediately but sessionContent only after an async fetch, so for a brief window
  // the new session is paired with the previous session's last-slide position. That
  // mismatch previously marked the freshly-opened (next) session/module complete.
  const loadedSessionContentRef = useRef(null);
  const positionUpdateIntervalRef = useRef(null);
  const lastPositionUpdateRef = useRef(null);

  // Attach root-level IntroVideo/IntroVideoVtt to VideoTemplate slides (always use root-level URL)
  const attachIntroVideoToSlides = (content, introVideoUrl, introVideoVttUrl) => {
    if (!content || !introVideoUrl) return content;

    const updated = {};
    Object.keys(content).forEach((segmentKey) => {
      const slides = content[segmentKey];
      if (!Array.isArray(slides)) {
        updated[segmentKey] = slides;
        return;
      }

      updated[segmentKey] = slides.map((slide) => {
        if (!slide || typeof slide !== 'object') return slide;
        if (slide.SlideLayout !== 'VideoTemplate') return slide;

        return {
          ...slide,
          VideoUrl: introVideoUrl,
          VideoVttUrl: introVideoVttUrl || null,
        };
      });
    });

    return updated;
  };

  // Helper function to get profile from localStorage if not available
  const getProfileFromStorage = () => {
    try {
      const org_code = localStorage.getItem(LOCAL_STORAGE_KEYS.ACTIVE_ORG_CODE);
      if (org_code) {
        const available_profiles = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.AVAILABLE_PROFILES));
        if (available_profiles) {
          return available_profiles.find(profile => profile.org_code === org_code);
        }
      }
    } catch (error) {
      console.error('Error loading profile from localStorage:', error);
    }
    return null;
  };

  // Position tracking utility functions
  // Memoize to prevent unnecessary re-renders and effect triggers
  const updateCurrentPosition = useCallback(async (session, segment, slide, immediate = false) => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }
    
    if (!currentProfile?._id || !identifier) return;
    
    try {
      const positionData = {
        current_position: {
          session: session,
          segment: segment,
          slide: slide
        }
      };
      
      await updateContentHistory(identifier, currentProfile._id, positionData);
      lastPositionUpdateRef.current = { session, segment, slide };
      setPositionUpdateError(null);
      
      if (immediate) {
        console.log('Immediate position update:', { session, segment, slide });
      }
    } catch (error) {
      console.error('Error updating position:', error);
      setPositionUpdateError('Failed to save progress');
    }
  }, [profile, identifier]); // Only recreate if profile or identifier changes

  // Update local programs state to reflect a session's completion WITHOUT an API
  // call. Used when completion has already been persisted server-side and we only
  // need the sidebar tick to reflect it immediately (e.g. the last/single
  // session, which is completed by the finishCourseAssessments flow rather than
  // by markSessionCompleted).
  const markSessionCompletedLocal = useCallback((sessionNo) => {
    setPrograms(prevPrograms =>
      prevPrograms.map(program => ({
        ...program,
        units: program.units?.map(unit => ({
          ...unit,
          sessions: unit.sessions?.map(session =>
            session.session_no === sessionNo
              ? { ...session, completed: true }
              : session
          )
        }))
      }))
    );
  }, []);

  const markSessionCompleted = useCallback(async (sessionNo) => {
    // Get profile - either from props or localStorage
    let currentProfile = profile;
    if (!currentProfile?._id) {
      currentProfile = getProfileFromStorage();
    }
    
    if (!currentProfile?._id || !identifier) return;
    
    try {
      const completionData = {
        session_completion: {
          session_no: sessionNo,
          completed: true
        }
      };
      
      await updateContentHistory(identifier, currentProfile._id, completionData);
      
      // Update local state to reflect completion
      markSessionCompletedLocal(sessionNo);

      // Score this session's pre/post assessment in the background once the
      // session is completed. The module-level finish averages these per-session
      // scores. Fire-and-forget: the backend is idempotent and the module finish
      // lazily re-scores any session that wasn't scored here, so failures here
      // are non-fatal. Module code is the leading segment of the session_no
      // (e.g. "M1_U1_S3" -> "M1").
      const programModuleCode = String(sessionNo).split('_')[0];
      finishSessionAssessmentsService(identifier, currentProfile._id, programModuleCode, sessionNo)
        .catch(error => console.error('Error scoring session assessment:', sessionNo, error));

      setPositionUpdateError(null);
    } catch (error) {
      console.error('Error marking session completed:', error);
      setPositionUpdateError('Failed to save completion status');
    }
  }, [profile, identifier, markSessionCompletedLocal]);

  // Session selection handler
  // Optional previousSessionToComplete parameter: if provided, sends both completion and position in one API call
  const handleSessionSelect = async (sessionNo, previousSessionToComplete = null) => {
    if (sessionNo === activeSessionNo) return;

    try {
      setActiveSessionNo(sessionNo);
      sessionJustLoadedRef.current = true; // Flag to prevent immediate auto-completion

      setSessionLoading(true);
      // const response = await fetchSessionContentMock(1);
      const response = await fetchLearningProgramContent(identifier, sessionNo);
      let content;
      if (response.data && response.data.content) {
        // Sort segments to ensure correct order (Seg1, Seg2, ..., SLO1, SLO2, ...)
        content = sortSessionContent(deriveSlideLayouts(response.data.content, sessionNo));
      }

      // // Extract and store IntroVideo if available
      // const introVideoUrl = content.IntroVideo;
      // if (introVideoUrl) {
      //   setIntroVideo(introVideoUrl);
      //   // Inject IntroVideo as first slide in Introduction segment
      //   content = injectIntroVideo(content, introVideoUrl);
      // }

      // // Remove IntroVideo property from content if it exists (it shouldn't be a segment)
      // if (content && content.IntroVideo) {
      //   const { IntroVideo, ...cleanedContent } = content;
      //   content = cleanedContent;
      // }
     // Attach root-level IntroVideo/IntroVideoVtt to VideoTemplate slides
      content = attachIntroVideoToSlides(content, response.data?.IntroVideo, response.data?.IntroVideoVtt);

      // Mark which session this content belongs to BEFORE publishing it, so
      // auto-completion can verify content matches the active session.
      loadedSessionContentRef.current = sessionNo;
      setSessionContent(content);
      
      // Store slo_titles if available
      if (response.data && response.data.slo_titles) {
        setSloTitles(response.data.slo_titles);
      }

      // // Store session_assessments if available
      // if (response.data && response.data.session_assessments) {
      //   setSessionAssessments(response.data.session_assessments);
      // }

      if (content) {
        const segments = Object.keys(content || {});
        const firstSegment = segments.find(seg => Array.isArray(content[seg]) && content[seg].length > 0);
        setActiveSegment(firstSegment || 'Seg1');
        setCurrentSlideIndex(0);
        
        // If previousSessionToComplete is provided, send both completion and position in ONE call
        if (previousSessionToComplete) {
          // Get profile - either from props or localStorage
          let currentProfile = profile;
          if (!currentProfile?._id) {
            currentProfile = getProfileFromStorage();
          }
          
          if (currentProfile?._id) {
            try {
              const historyData = {
                current_position: {
                  session: sessionNo,
                  segment: firstSegment || 'Seg1',
                  slide: 0
                },
                session_completion: {
                  session_no: previousSessionToComplete,  // Mark PREVIOUS session as complete
                  completed: true
                }
              };
              
              await updateContentHistory(identifier, currentProfile._id, historyData);
              lastPositionUpdateRef.current = { session: sessionNo, segment: firstSegment || 'Seg1', slide: 0 };
              setPositionUpdateError(null);

              // Score the session being left, the same way markSessionCompleted does,
              // so per-session scores are produced via this navigation path too.
              // Fire-and-forget + idempotent backend, so a duplicate call is harmless.
              const prevModuleCode = String(previousSessionToComplete).split('_')[0];
              finishSessionAssessmentsService(identifier, currentProfile._id, prevModuleCode, previousSessionToComplete)
                .catch(err => console.error('Error scoring session assessment:', previousSessionToComplete, err));
            } catch (error) {
              console.error('Error updating history with completion:', error);
              setPositionUpdateError('Failed to save progress');
            }
          }
        } else {
          // Normal position update only (no completion)
          updateCurrentPosition(sessionNo, firstSegment || 'Seg1', 0, true);
        }
      }
    } catch (error) {
      console.error('Error fetching session content:', error);
    } finally {
      setSessionLoading(false);
    }
  };

  // Initial data fetching
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Get profile - either from props or localStorage
        let currentProfile = profile;
        if (!currentProfile?._id) {
          currentProfile = getProfileFromStorage();
        }

        // If still no profile with _id, wait for it to be loaded
        if (!currentProfile?._id || !identifier) {
          console.log('Waiting for profile to be loaded...', { profile: currentProfile, identifier });
          return;
        }

        // Fetch program layout
        setLayoutLoading(true);
        const response = await fetchLearningProgramOutline(identifier, currentProfile._id);
        setCourseTitle(response.data.course_title);
        const layoutData = response.data.program_layout;
        const currentPosition = response.data.current_position;
        const lastMessageRead = response.data.last_message_read;
        const discussionSessionsData = response.data.discussion_sessions || [];
        setProgramLayout(layoutData);
        setLastMessageReadTimestamp(lastMessageRead);
        setDiscussionSessions(discussionSessionsData);

        // Transform data for easier access
        const programsArray = Object.keys(layoutData).map(key => layoutData[key]);
        setPrograms(programsArray);

        // Check if we should resume from a previous position
        let resumeSession = null;
        let resumeSegment = null;
        let resumeSlide = 0;
        
        if (currentPosition && currentPosition.session) {
          // Find the session in the program layout to validate it exists
          let sessionExists = false;
          for (const program of programsArray) {
            for (const unit of program.units || []) {
              for (const session of unit.sessions || []) {
                if (session.session_no === currentPosition.session) {
                  sessionExists = true;
                  resumeSession = currentPosition.session;
                  resumeSegment = currentPosition.segment;
                  resumeSlide = currentPosition.slide || 0;
                  break;
                }
              }
              if (sessionExists) break;
            }
            if (sessionExists) break;
          }
        }

        // Set default expanded program and unit, and load session content
        if (programsArray.length > 0) {
          let targetSession = null;
          let targetSegment = null;
          let targetSlide = 0;
          
          if (resumeSession) {
            // Resume from saved position
            targetSession = resumeSession;
            targetSegment = resumeSegment;
            targetSlide = resumeSlide;
            setActiveSessionNo(targetSession);
            sessionJustLoadedRef.current = true;
          } else {
            // Start from the beginning
            const firstProgram = programsArray[0];
            if (firstProgram.units && firstProgram.units.length > 0) {
              const firstUnit = firstProgram.units[0];
              if (firstUnit.sessions && firstUnit.sessions.length > 0) {
                const firstSession = firstUnit.sessions[0];
                targetSession = firstSession.session_no;
                setActiveSessionNo(targetSession);
                sessionJustLoadedRef.current = true;
              }
            }
          }

          // Fetch session content
          if (targetSession) {
            setSessionLoading(true);
            // const response = await fetchSessionContentMock(1);
            const response = await fetchLearningProgramContent(identifier, targetSession);
            let content;
            if (response.data && response.data.content) {
              // Sort segments to ensure correct order (Seg1, Seg2, ..., SLO1, SLO2, ...)
              content = sortSessionContent(deriveSlideLayouts(response.data.content, targetSession));
            }

            // // Extract and store IntroVideo if available
            // const introVideoUrl = content.IntroVideo;
            // if (introVideoUrl) {
            //   setIntroVideo(introVideoUrl);
            //   // Inject IntroVideo as first slide in Introduction segment
            //   content = injectIntroVideo(content, introVideoUrl);
            // }
            //
            // // Remove IntroVideo property from content if it exists (it shouldn't be a segment)
            // if (content && content.IntroVideo) {
            //   const { IntroVideo, ...cleanedContent } = content;
            //   content = cleanedContent;
            // }

            // Attach root-level IntroVideo/IntroVideoVtt to VideoTemplate slides
            content = attachIntroVideoToSlides(content, response.data?.IntroVideo, response.data?.IntroVideoVtt);

            // Mark which session this content belongs to BEFORE publishing it.
            loadedSessionContentRef.current = targetSession;
            setSessionContent(content);
            console.log('Fetched session content:', content);

            // Store slo_titles if available
            if (response.data && response.data.slo_titles) {
              setSloTitles(response.data.slo_titles);
            }

            // // Store session_assessments if available
            // if (response.data && response.data.session_assessments) {
            //   setSessionAssessments(response.data.session_assessments);
            // }

            if (content) {
              // Set segment and slide
              if (targetSegment && content[targetSegment]) {
                // Resume with specific segment and slide
                setActiveSegment(targetSegment);
                setCurrentSlideIndex(Math.min(targetSlide, content[targetSegment].length - 1));
              } else {
                // Start from first available segment
                const firstSegment = Object.keys(content).find(
                  key => Array.isArray(content[key]) && content[key].length > 0
                );
                setActiveSegment(firstSegment || null);
                setCurrentSlideIndex(0);
              }
            }

            setSessionLoading(false);
          }
        }

        setLayoutLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [identifier, profile?._id, profile]);

  // // Helper function to inject IntroVideo as first slide in Introduction segment
  // const injectIntroVideo = (content, videoUrl) => {
  //   if (!videoUrl || !content) return content;
  //
  //   const modifiedContent = { ...content };
  //
  //   // Find the Introduction segment (could be 'Introduction', 'Intro', or 'Seg1')
  //   const introSegmentKey = Object.keys(modifiedContent).find(key =>
  //     key.toLowerCase().includes('intro') || key === 'Seg1'
  //   ) || 'Introduction';
  //
  //   // Create the intro video slide
  //   const videoSlide = {
  //     SlideOrder: 0,
  //     SlideLayout: 'VideoTemplate',
  //     SlideTitle: '',
  //     SlideContent: '',
  //     VideoUrl: videoUrl,
  //     Images: []
  //   };
  //
  //   // Inject as first slide in the segment
  //   if (modifiedContent[introSegmentKey]) {
  //     modifiedContent[introSegmentKey] = [videoSlide, ...modifiedContent[introSegmentKey]];
  //   } else {
  //     modifiedContent[introSegmentKey] = [videoSlide];
  //   }
  //
  //   return modifiedContent;
  // };

  // Update session data (for discussion sessions, etc.)
  const updateSessionData = (updates) => {
    if (updates.discussionSessions) {
      setDiscussionSessions(updates.discussionSessions);
    }
  };

  return {
    // State
    loading,
    layoutLoading,
    sessionLoading,
    programLayout,
    programs,
    sessionContent,
    sloTitles,
    courseTitle,
    activeSessionNo,
    activeSegment,
    currentSlideIndex,
    positionUpdateError,
    sessionAssessments,

    // introVideo,

    // Setters
    setPrograms,
    setActiveSegment,
    setCurrentSlideIndex,
    setPositionUpdateError,
    
    // Discussion-related
    lastMessageReadTimestamp,
    updateLastMessageReadTimestamp: setLastMessageReadTimestamp,
    discussionSessions,
    
    // Functions
    updateCurrentPosition,
    markSessionCompleted,
    markSessionCompletedLocal,
    handleSessionSelect,
    updateSessionData,
    
    // Refs
    sessionJustLoadedRef,
    loadedSessionContentRef,
    positionUpdateIntervalRef,
    lastPositionUpdateRef
  };
};
